// Dramatic player death sequence, driven by REAL render time (not sim ticks)
// so the 0.22× slow-motion dilation can't stretch it into a crawl:
//   IGNITE   0.25 s — white-hot flash + violent blaze burst at full speed
//   BLAZE    1.60 s — embers and warm smoke rise off the wreck, glow pulses
//   AFTERGLOW 1.15 s — gold ring expands and fades, embers thin to nothing
// Total ≈ 3.0 s. Fire palette adapted from the canvasui.dev blaze component.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';
import { ParticleSystem } from './particles.js';
import { DeathRings } from './death-rings.js';
import { easeOutCubic, easeInCubic } from './easing.js';

const IGNITE_S = 0.25;
const BLAZE_S = 1.6;
const AFTERGLOW_S = 1.15;
/** Total real seconds; game.ts paces the game-over panel to this. */
export const PLAYER_DEATH_DURATION_S = IGNITE_S + BLAZE_S + AFTERGLOW_S;

/** Soft radial glow (white core → transparent edge) shared by fire meshes. */
let radialGlowTexture: THREE.CanvasTexture | null = null;
function makeRadialGlowTexture(): THREE.CanvasTexture {
  if (radialGlowTexture) return radialGlowTexture;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  radialGlowTexture = new THREE.CanvasTexture(canvas);
  return radialGlowTexture;
}

export class PlayerDeathFX {
  private active = false;
  private elapsed = 0;
  private emberClock = 0;
  private pos: Vec2 = { x: 0, y: 0 };
  private flashMesh: THREE.Mesh;
  private flashMat: THREE.MeshBasicMaterial;
  private glowMesh: THREE.Mesh;
  private glowMat: THREE.MeshBasicMaterial;
  private fireMesh: THREE.Mesh;
  private fireMat: THREE.MeshBasicMaterial;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly particles: ParticleSystem,
    private readonly deathRings: DeathRings
  ) {
    // Big white flash that covers the screen briefly.
    const flashGeo = new THREE.PlaneGeometry(200, 200);
    this.flashMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
    });
    this.flashMesh = new THREE.Mesh(flashGeo, this.flashMat);
    this.flashMesh.position.z = 10;
    this.flashMesh.visible = false;
    scene.add(this.flashMesh);

    // Lingering afterglow: a soft gold ring that slowly expands and fades.
    const glowGeo = new THREE.RingGeometry(0.8, 1.0, 48);
    this.glowMat = new THREE.MeshBasicMaterial({
      color: 0xffd36b,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.glowMesh = new THREE.Mesh(glowGeo, this.glowMat);
    this.glowMesh.visible = false;
    scene.add(this.glowMesh);

    // Wreck fire: a soft additive orange glow that burns while embers rise —
    // radial-gradient texture so it reads as fire, not a hard-edged coin.
    const fireGeo = new THREE.CircleGeometry(1, 40);
    this.fireMat = new THREE.MeshBasicMaterial({
      color: 0xff7820,
      map: makeRadialGlowTexture(),
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.fireMesh = new THREE.Mesh(fireGeo, this.fireMat);
    this.fireMesh.visible = false;
    scene.add(this.fireMesh);
  }

  start(pos: Vec2, tick: number): void {
    this.active = true;
    this.elapsed = 0;
    this.emberClock = 0;
    this.pos = { x: pos.x, y: pos.y };

    // Ignite: huge white flash, blaze eruption, expanding rings.
    this.flashMesh.visible = true;
    this.flashMesh.position.set(pos.x, pos.y, 10);
    this.flashMat.opacity = 1;

    this.particles.spawnBlaze(pos);
    this.particles.spawnDeathShards(pos, 0xffd36b);
    // Hull shards: the ship visibly breaks apart in its own colors.
    this.particles.spawnDeathShards(pos, 0x9fefff);
    this.deathRings.spawn(pos, new THREE.Color(0xff9540), tick);
    this.deathRings.spawn(pos, new THREE.Color(0xffffff), tick);

    // Afterglow ring starts small.
    this.glowMesh.visible = true;
    this.glowMesh.position.set(pos.x, pos.y, 0.08);
    this.glowMesh.scale.set(0.1, 0.1, 1);
    this.glowMat.opacity = 0.8;

    // Wreck fire ignites with the burst.
    this.fireMesh.visible = true;
    this.fireMesh.position.set(pos.x, pos.y, 0.09);
    this.fireMesh.scale.set(1.0, 1.0, 1);
    this.fireMat.opacity = 0.85;
  }

  /** Advance in REAL seconds; returns true once the sequence is done. */
  update(dt: number): boolean {
    if (!this.active) return true;
    this.elapsed += dt;
    const t = this.elapsed;

    // IGNITE: the white flash holds at full blast, then snaps out fast.
    if (t < IGNITE_S) {
      const hold = 0.08;
      const k = t < hold ? 0 : (t - hold) / (IGNITE_S - hold);
      this.flashMat.opacity = 1 - easeOutCubic(Math.min(1, k));
      return false;
    }
    this.flashMat.opacity = 0;

    // BLAZE: embers + smoke stream off the wreck; fire glow pulses; the gold
    // ring expands slowly.
    const tb = t - IGNITE_S;
    if (tb < BLAZE_S) {
      const k = tb / BLAZE_S;
      this.emberClock += dt;
      // ~110 sparks/s at the start, thinning to ~25/s as the fire dies.
      const rate = 0.009 + k * 0.031;
      while (this.emberClock >= rate) {
        this.emberClock -= rate;
        this.particles.spawnEmber(this.pos);
      }
      const scale = 0.1 + easeOutCubic(k) * 2.4;
      // Firelight pulse: glow and wreck fire breathe while the embers burn.
      const pulse = 0.8 + 0.2 * Math.sin(t * 9);
      this.glowMesh.scale.set(scale, scale, 1);
      this.glowMat.opacity = 0.8 * (1 - easeOutCubic(k) * 0.5) * pulse;
      this.fireMesh.scale.set(1.0 + easeOutCubic(k) * 0.9, 1.0 + easeOutCubic(k) * 0.9, 1);
      this.fireMat.opacity = 0.85 * (1 - k * 0.6) * pulse;
      return false;
    }

    // AFTERGLOW: embers stop; fire out; the ring expands and fades to nothing.
    const ta = tb - BLAZE_S;
    if (ta < AFTERGLOW_S) {
      const k = ta / AFTERGLOW_S;
      const scale = 2.5 + easeInCubic(k) * 1.5;
      this.glowMesh.scale.set(scale, scale, 1);
      this.glowMat.opacity = 0.4 * (1 - easeInCubic(k));
      this.fireMat.opacity = 0.3 * (1 - easeInCubic(k));
      return false;
    }

    this.active = false;
    this.flashMesh.visible = false;
    this.glowMesh.visible = false;
    this.fireMesh.visible = false;
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    this.flashMesh.geometry.dispose();
    this.flashMat.dispose();
    this.glowMesh.geometry.dispose();
    this.glowMat.dispose();
    this.fireMesh.geometry.dispose();
    this.fireMat.dispose();
  }
}
